// Subdomain-based role routing utility
// Maps subdomains to user roles for multi-tenant architecture

export type SubdomainRole = 'superadmin' | 'admin' | 'merchant' | 'support' | 'analyst' | 'customer' | null;

export const SUBDOMAIN_CONFIG = {
  superadmin: 'master',
  admin: 'admin',
  merchant: 'merchant',
  support: 'support',
  analyst: 'analytics',
  customer: 'shop'
} as const;

/**
 * Detects the current subdomain from the window location
 * @returns The detected subdomain or null if on main domain
 */
export const detectSubdomain = (): SubdomainRole => {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  // Local development - check for custom subdomains on localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  
  // Handle custom subdomains on localhost (e.g., superadmin.127.0.0.1)
  if (hostname.endsWith('.127.0.0.1') || hostname.endsWith('.localhost')) {
    const subdomain = hostname.split('.')[0].toLowerCase();
    
    // Map subdomain to role
    for (const [role, configSubdomain] of Object.entries(SUBDOMAIN_CONFIG)) {
      if (subdomain === configSubdomain) {
        return role as SubdomainRole;
      }
    }
  }
  
  // Check if we have a subdomain (at least 3 parts: subdomain.domain.tld)
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    
    // Map subdomain to role
    for (const [role, configSubdomain] of Object.entries(SUBDOMAIN_CONFIG)) {
      if (subdomain === configSubdomain) {
        return role as SubdomainRole;
      }
    }
  }
  
  return null;
};

/**
 * Gets the expected subdomain for a given role
 * @param role - The user role
 * @returns The subdomain for that role or null
 */
export const getSubdomainForRole = (role: string): string | null => {
  return SUBDOMAIN_CONFIG[role as keyof typeof SUBDOMAIN_CONFIG] || null;
};

/**
 * Checks if the current subdomain matches the user's role
 * @param userRole - The user's role
 * @returns True if subdomain matches role or if no subdomain restriction
 */
export const isSubdomainMatch = (userRole: string): boolean => {
  const currentSubdomain = detectSubdomain();
  
  // If no subdomain detected, allow access
  if (!currentSubdomain) return true;
  
  // Check if current subdomain matches user role
  return currentSubdomain === userRole;
};

/**
 * Redirects to the appropriate subdomain based on role
 * @param role - The user's role
 */
export const redirectToSubdomain = (role: string): void => {
  if (typeof window === 'undefined') return;
  
  const subdomain = getSubdomainForRole(role);
  if (!subdomain) return;
  
  const currentHostname = window.location.hostname;
  const parts = currentHostname.split('.');
  
  // If already on correct subdomain, do nothing
  if (parts[0] === subdomain) return;
  
  // Construct new hostname with subdomain
  const newHostname = parts.length >= 3 
    ? [subdomain, ...parts.slice(1)].join('.')
    : [subdomain, ...parts].join('.');
  
  // Redirect to new subdomain
  window.location.href = `${window.location.protocol}//${newHostname}${window.location.pathname}${window.location.search}`;
};
